/**
 * adminAuth.js
 *
 * Handles fetching / creating admin records from the `adminUsers` Firestore
 * collection.  This collection is separate from the customer-facing `users`
 * collection so that org / vendor admins never mix with end-users.
 *
 * adminUsers document shape:
 * {
 *   uid:         string  (Firebase Auth UID)
 *   email:       string
 *   name:        string
 *   role:        "super_admin" | "admin" | "read_only" | "salon_super_admin" | "salon_admin"
 *   vendorId:    string | null  (only for salon_* roles)
 *   vendorName:  string | null  (display name of the vendor)
 *   status:      "active" | "inactive" | "suspended"
 *   createdAt:   Timestamp
 *   lastLoginAt: Timestamp
 * }
 */

import firebase from "../firebase";

const COLLECTION = "adminUsers";

/**
 * Fetch the adminUsers document for the given Firebase Auth UID.
 * Strategy:
 *  1. Try document ID == uid (ideal setup)
 *  2. Fall back to querying the `uid` field (document created with a different ID)
 *  3. Fall back to querying by email
 * Returns null when no document exists (user is not an admin).
 */
export const fetchAdminRecord = async (uid, email) => {
  // 1. Direct document lookup
  const directDoc = await firebase
    .firestore()
    .collection(COLLECTION)
    .doc(uid)
    .get();

  if (directDoc.exists) {
    return { id: directDoc.id, ...directDoc.data() };
  }

  // 2. Query by uid field
  const byUidSnap = await firebase
    .firestore()
    .collection(COLLECTION)
    .where("uid", "==", uid)
    .limit(1)
    .get();

  if (!byUidSnap.empty) {
    const d = byUidSnap.docs[0];
    return { id: d.id, ...d.data() };
  }

  // 3. Query by email (last resort)
  if (email) {
    const byEmailSnap = await firebase
      .firestore()
      .collection(COLLECTION)
      .where("email", "==", email)
      .limit(1)
      .get();

    if (!byEmailSnap.empty) {
      const d = byEmailSnap.docs[0];
      return { id: d.id, ...d.data() };
    }
  }

  return null;
};

/**
 * Persist the admin's role and vendorId in localStorage so RBAC helpers
 * (getUserRole / getVendorId) work synchronously throughout the app.
 */
export const storeAdminSession = (adminRecord) => {
  localStorage.setItem("adminRole", adminRecord.role);
  localStorage.setItem("adminVendorId", adminRecord.vendorId || "");
  localStorage.setItem("adminName", adminRecord.name || adminRecord.email);
  localStorage.setItem("adminUid", adminRecord.uid || adminRecord.id);
  localStorage.setItem("loginTime", Date.now().toString());
};

/**
 * Clear the admin session from localStorage.
 */
export const clearAdminSession = () => {
  ["adminRole", "adminVendorId", "adminName", "adminUid", "loginTime"].forEach(
    (k) => localStorage.removeItem(k)
  );
};

/**
 * Update the admin record's lastLoginAt field.
 */
export const updateLastLogin = (uid) =>
  firebase
    .firestore()
    .collection(COLLECTION)
    .doc(uid)
    .update({ lastLoginAt: firebase.firestore.FieldValue.serverTimestamp() })
    .catch(() => {}); // non-critical

// ── CRUD for the RoleManagement page ────────────────────────────────────────

/**
 * Subscribe to all adminUsers documents (real-time).
 * Returns the unsubscribe function.
 */
export const subscribeAdminUsers = (callback) =>
  firebase
    .firestore()
    .collection(COLLECTION)
    .orderBy("createdAt", "desc")
    .onSnapshot((snapshot) => {
      const admins = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(admins);
    });

/**
 * Create a new adminUsers record for an existing Firebase Auth user.
 * The caller must already have created the Auth account (or invited the user).
 */
export const createAdminUser = async ({
  uid,
  email,
  name,
  role,
  vendorId = null,
  vendorName = null,
}) => {
  const isVendorRole = role === "salon_super_admin" || role === "salon_admin";

  await firebase
    .firestore()
    .collection(COLLECTION)
    .doc(uid)
    .set({
      uid,
      email,
      name,
      role,
      vendorId: isVendorRole ? vendorId : null,
      vendorName: isVendorRole ? vendorName : null,
      status: "active",
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      lastLoginAt: null,
    });
};

/**
 * Update an existing adminUsers record (role, vendorId, status, name).
 */
export const updateAdminUser = async (uid, updates) => {
  const isVendorRole =
    updates.role === "salon_super_admin" || updates.role === "salon_admin";

  // If switching to an org role, clear vendor fields
  if (updates.role && !isVendorRole) {
    updates.vendorId = null;
    updates.vendorName = null;
  }

  await firebase
    .firestore()
    .collection(COLLECTION)
    .doc(uid)
    .update(updates);
};

/**
 * Soft-delete: set status to "suspended".
 */
export const suspendAdminUser = (uid) =>
  firebase
    .firestore()
    .collection(COLLECTION)
    .doc(uid)
    .update({ status: "suspended" });
